<template>
  <div class="container v-content-container popup-wrapper">
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-list-alt"></i>
          <span v-if="selectedEventPriceId != null">
            {{ noDataExist ? "Event price" : selectedEventPrice.name }}
          </span>
          <span v-else-if="!loading">
            {{ $label("event.new_event_price", "New event price") }}
          </span>
        </h1>
      </div>
    </div>

    <navigation-tabs :event-id="selectedEventId"></navigation-tabs>

    <div class="row">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <error404 v-if="noDataExist" />

    <template v-else>
      <div v-show="loading" class="row">
        <div class="col-xs-12">
          <view-loader
            margin-top-pixels="15"
            border-color="primary"
          ></view-loader>
        </div>
      </div>

      <div v-show="!loading" id="event-price-form">
        <section>
          <div class="row">
            <div class="col-sm-3 col-xs-12">
              <h2>
                <i class="fa fa-fw fa-lg fa-money"></i>
                {{ $label("event.course_price", "Course Price") }}
              </h2>
            </div>
            <div class="col-sm-6 col-xs-12 mt-6">
              <div class="v-form-partial">
                <div class="v-form">
                  <fieldset>
                    <div class="row">
                      <!-- BEGIN Price name -->
                      <section class="col col-12">
                        <text-input
                          v-model="selectedEventPrice.name"
                          id="price-name"
                          :label="$label('event.designation', 'designation')"
                          :is-disabled="updating"
                          :has-error="hasError('name')"
                          :error="getError('name')"
                          @keyup.native.enter="createOrUpdatePrice"
                        />
                      </section>
                      <!-- END Price name -->
                    </div>
                    <div class="row">
                      <!-- BEGIN Price form -->
                      <section class="col col-4">
                        <label class="label">
                          {{ $label("event.price_input", "input") }}
                        </label>

                        <label class="select">
                          <select
                            id="price-form"
                            :disabled="updating"
                            v-model="selectedEventPrice.form"
                          >
                            <option value="net">Net</option>
                            <option value="gross">Gross</option>
                          </select>
                          <i></i>
                        </label>

                        <p v-if="hasError('form')" class="text-danger">
                          {{ getError("form") }}
                        </p>
                      </section>
                      <!-- END Price form -->
                      <!-- BEGIN Price VAT-->
                      <section class="col col-4">
                        <label class="label">
                          {{ $label("event.price_vat", "VAT") }}
                        </label>

                        <label class="select">
                          <select
                            id="price-vat"
                            :disabled="updating"
                            v-model="selectedEventPrice.vat_id"
                          >
                            <option
                              v-for="vat in availableVats"
                              :value="vat.id"
                              :key="vat.id"
                              >{{ vat.name }}</option
                            >
                          </select>
                          <i></i>
                        </label>

                        <p v-if="hasError('vat_id')" class="text-danger">
                          {{ getError("vat_id") }}
                        </p>
                      </section>
                      <!-- END Price VAT -->
                      <!-- BEGIN Price amount-->
                      <section class="col col-4">
                        <text-input
                          v-model="selectedEventPrice.amount"
                          id="price-amount"
                          :label="$label('event.course_price', 'Price')"
                          :placeholder="$label('event.course_price', 'Price')"
                          :is-disabled="updating"
                          :has-error="hasError('amount')"
                          :error="getError('amount')"
                          hasIcon="true"
                          icon="fa fa-euro"
                          @keyup.native.enter="createOrUpdatePrice"
                        />
                      </section>
                      <!-- END Price amount -->
                    </div>
                    <div class="row">
                      <!-- BEGIN Price description -->
                      <section class="col col-12">
                        <label class="label">
                          {{ $label("event.price_description", "description") }}
                        </label>
                        <label class="textarea">
                          <textarea
                            v-model="selectedEventPrice.description"
                            :class="{
                              'text-input-error': hasError('description')
                            }"
                            :placeholder="
                              $label('event.price_description', 'description')
                            "
                            :disabled="updating"
                            id="price-description"
                            rows="5"
                          >
                          </textarea>
                        </label>
                        <p v-if="hasError('description')" class="text-danger">
                          {{ getError("description") }}
                        </p>
                      </section>
                      <!-- END Price description -->
                    </div>
                    <div class="row">
                      <!--BEGIN Price usable online-->
                      <section class="col col-8" style="height: 50px">
                        <label class="mb-4 input">
                          View online
                        </label>
                        <label class="label">
                          Display this price online
                        </label>
                      </section>
                      <section class="col col-4">
                        <div
                          class="toggle-button pull-right bottom mt-6"
                          @click="
                            selectedEventPrice.usable_online = !selectedEventPrice.usable_online
                          "
                          id="price-usable-online"
                          :value="selectedEventPrice.usable_online"
                        >
                          <template v-if="selectedEventPrice.usable_online">
                            <i class="fa fa-circle text-green"></i>
                            <label class="input">
                              {{ $label("common.yes", "Yes") }}
                            </label>
                          </template>
                          <template v-else>
                            <label class="input">
                              {{ $label("common.no", "No") }}
                            </label>
                            <i class="fa fa-circle text-green"></i>
                          </template>
                        </div>
                      </section>
                      <!--END Price usable online-->
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div class="row mb-4">
        <div class="col-xs-12">&nbsp;</div>
      </div>
      <hr />

      <div class="row">
        <div class="col-xs-12 text-center">
          <back-button
            back-link="EventPrices"
            :class="{ 'is-disabled': updating }"
            id="back-event-date-settings-button"
            icon="fa fa-lg fa-fw fa-arrow-circle-o-left"
            :text="$label('common.back', 'Back')"
          >
          </back-button>
          &nbsp;
          <info-button
            :class="{ 'is-disabled': updating }"
            id="save-price-button"
            icon="fa fa-lg fa-fw fa-save"
            :text="$label('common.save', 'Save')"
            @click.native="createOrUpdatePrice"
          >
          </info-button>
          &nbsp;
          <danger-button
            :class="{
              'is-disabled': updating || selectedEventPriceId == null
            }"
            id="delete-user-button"
            icon="fa fa-fw fa-lg fa-trash-o"
            :text="$label('common.delete', 'Delete')"
            @click.native="deleteEventPrice"
          >
          </danger-button>
        </div>
        <div class="row mb-4">
          <div class="col-xs-12">&nbsp;</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Api from "@/api.js";
import FormErrorsMixin from "@/mixins/FormErrors";

import NavigationTabs from "@/components/Settings/Events/EventDetails/NavigationTabs.vue";
import ViewLoader from "@/components/Common/ViewLoader";
import TextInput from "@/components/Common/Inputs/TextInput.vue";
import InfoButton from "@/components/Common/Buttons/InfoButton";
import BackButton from "@/components/Common/Buttons/BackButton";
import DangerButton from "@/components/Common/Buttons/DangerButton";
import Error404 from "@/components/Common/Error404";
import swal from "sweetalert";

export default {
  name: "EventPriceDetails.vue",

  mixins: [FormErrorsMixin],

  components: {
    ViewLoader,
    NavigationTabs,
    TextInput,
    InfoButton,
    BackButton,
    DangerButton,
    Error404
  },

  data() {
    return {
      loading: false,
      updating: false,
      noDataExist: false,
      availableVats: null,
      selectedEvent: {
        name: null
      },
      selectedEventId: null,
      selectedEventPriceId: null,
      selectedEventPrice: {
        type: "secondary",
        name: null,
        description: null,
        form: "net",
        amount: 0,
        vat_id: 1,
        usable_online: false,
        customerGroup: "External"
      },
      validatedInputs: ["name", "description", "form", "vat_id", "amount"]
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedEventId = vm.$route.params.event_id;
      vm.selectedEventPriceId = vm.$route.params.price_id;

      // Get Event
      if (
        typeof vm.selectedEventId !== "undefined" &&
        vm.selectedEventId !== null
      ) {
        vm.getEvent(() => {
          //
        });
      }

      // Get Secondary Price
      if (
        typeof vm.selectedEventPriceId !== "undefined" &&
        vm.selectedEventPriceId !== null
      ) {
        if (vm.$route.query.event_price_created) {
          swal("Success!", "The price was created.", "success").then(() => {
            window.history.pushState(
              {},
              document.title,
              window.location.pathname
            );
          });
        }
        vm.getEventPrice(() => {
          //
        });
      } else {
        // Get Default Price
        vm.getDefaultPrice(() => {
          //
        });
      }
      // Get Available vats
      vm.getVats(() => {
        //
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    // Get Event
    if (
      typeof this.selectedEventId !== "undefined" &&
      this.selectedEventId !== null
    ) {
      this.getEvent(() => {
        //
      });
    }

    // Get Secondary Price
    if (
      typeof this.selectedEventPriceId !== "undefined" &&
      this.selectedEventPriceId !== null
    ) {
      this.getEventPrice(() => {
        //
      });
    } else {
      // Get Default Price
      this.getDefaultPrice(() => {
        //
      });
    }
    // Get Available vats
    this.getVats(() => {
      //
    });
    next();
  },

  mounted() {
    //
  },

  methods: {
    createOrUpdatePrice() {
      if (this.selectedEventPriceId == null) {
        this.createEventPrice();
      } else {
        this.updateEventPrice();
      }
    },

    getEvent() {
      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint("events/" + this.selectedEventId);

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedEvent = response.data.data;
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          console.log(error.response);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getEventPrice() {
      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint(
        "events/" +
          this.selectedEventId +
          "/prices/" +
          this.selectedEventPriceId
      );

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedEventPrice = response.data.data;
          this.selectedEventPrice.amount =
            response.data.data.form === "net"
              ? response.data.data.amount_net
              : response.data.data.amount_gross;
          this.selectedEventPrice.vat_id = response.data.data.vat.id;
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updateEventPrice() {
      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint(
        "events/" +
          this.selectedEventId +
          "/prices/" +
          this.selectedEventPriceId
      );

      Api()
        .put(endpoint, this.selectedEventPrice)
        .then(response => {
          this.selectedEventPriceId = response.data.data.id;
          this.getEventPrice();
        })
        .catch(error => {
          console.log(error);
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    createEventPrice() {
      if (this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint(
        "events/" + this.selectedEventId + "/prices"
      );

      Api()
        .post(endpoint, this.selectedEventPrice)
        .then(response => {
          this.selectedEventPriceId = response.data.data.id;
          this.$router.push({
            name: "EventPriceDetails",
            params: {
              event_id: this.selectedEventId,
              price_id: this.selectedEventPriceId
            },
            query: { event_price_created: true }
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    deleteEventPrice() {
      if (this.loading || this.updating) {
        return;
      }

      swal({
        title: "Are you sure?",
        text: "The price will be deleted and will not be able to use it",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "events/" +
              this.selectedEventId +
              "/prices/" +
              this.selectedEventPriceId
          );

          Api()
            .delete(endpoint)
            .then(response => {
              console.log(response.status);
              this.$router.push({
                name: "EventPrices",
                query: { event_price_deleted: true }
              });
            })
            .catch(error => {
              console.log(error.response);
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete event prices.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    getDefaultPrice() {
      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint(
        "events/" + this.selectedEventId + "/prices/default"
      );

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedEventPrice.type = response.data.data.type;
          this.selectedEventPrice.usable_online =
            response.data.data.usable_online;
          this.selectedEventPrice.form = response.data.data.form;
          this.selectedEventPrice.amount =
            response.data.data.form === "net"
              ? response.data.data.amount_net
              : response.data.data.amount_gross;
          this.selectedEventPrice.vat_id = response.data.data.vat.id;
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getVats() {
      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint("vats");

      Api()
        .get(endpoint)
        .then(response => {
          this.availableVats = response.data.data;
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style scoped>
.mb-4 {
  margin-bottom: 1.2rem;
}

.mt-6 {
  margin-top: 1.8rem;
}

.toggle-button {
  width: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 3px solid #3aa94c;
  padding: 2px 5px;
  cursor: pointer;
}

.toggle-button * {
  cursor: pointer;
}

@media screen and (max-width: 600px) {
  .toggle-button {
    margin-top: 0;
    margin-bottom: 1.3rem;
  }
}
</style>
